import React, { useState } from 'react'
import { Button, Card } from '../components/ui'
import { API_BASE_URL } from '../data'
import type { AdminSettingsMetaProps } from '../data/types'
import { useGlobalSettings } from '../contexts'
import { Link } from 'react-router-dom'
import { ChevronLeftIcon } from '../icons'

type Props = {
    meta: AdminSettingsMetaProps
}

export const AdminSettings = (props: Props) => {
    const { meta } = props
    const { settings, loading: loadingSettings, updateSetting } = useGlobalSettings()
    const [loading, setLoading] = useState(false)
    const [clearingProfiles, setClearingProfiles] = useState(false)
    const [updatingFlag, setUpdatingFlag] = useState(false)

    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    const handleToggleVisitAnnounced = async () => {
        setUpdatingFlag(true)
        setMessage(null)

        try {
            const newValue = !settings.visit_announced
            await updateSetting('visit_announced', newValue)

            setMessage({
                type: 'success',
                text: `Visit announcement ${newValue ? 'enabled' : 'disabled'} successfully`
            })
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setUpdatingFlag(false)
        }
    }

    const handleToggleInternationalSignups = async () => {
        setUpdatingFlag(true)
        setMessage(null)

        try {
            const newValue = !settings.international_signups_enabled
            await updateSetting('international_signups_enabled', newValue)
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setUpdatingFlag(false)
        }
    }

    const handleToggleShowShiftInfo = async () => {
        setUpdatingFlag(true)
        setMessage(null)

        try {
            const newValue = !settings.show_shift_info
            await updateSetting('show_shift_info', newValue)

            setMessage({
                type: 'success',
                text: `Shift information display ${newValue ? 'enabled' : 'disabled'} successfully`
            })
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setUpdatingFlag(false)
        }
    }

    const handleToggleLocalMode = async () => {
        setUpdatingFlag(true)
        setMessage(null)

        try {
            const newValue = !settings.use_local_mode
            await updateSetting('use_local_mode', newValue)

            setMessage({
                type: 'success',
                text: `Local mode ${newValue ? 'enabled' : 'disabled'} successfully`
            })
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setUpdatingFlag(false)
        }
    }

    const handleAssignMandatoryTrainings = async () => {
        setLoading(true)
        setMessage(null)

        try {
            const response = await fetch(`${API_BASE_URL}/training/assign-mandatory-to-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to assign trainings')
            }

            setMessage({
                type: 'success',
                text: result.message || `Successfully assigned ${result.assigned} training(s)`
            })
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClearAllProfiles = async () => {
        setClearingProfiles(true)
        setMessage(null)

        try {
            const response = await fetch(`${API_BASE_URL}/volunteer/clear-profile-submissions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to clear profiles')
            }

            setMessage({
                type: 'success',
                text: result.message || `Successfully cleared ${result.count} profile(s)`
            })
        } catch (err) {
            setMessage({
                type: 'error',
                text: err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            setClearingProfiles(false)
        }
    }

    return (
        <div className="flex-1 container mx-auto px-4 py-8">
            <div className='mb-3'>
                <Link
                    to="/admin"
                    className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                >
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    Back to Admin Hub
                </Link>
            </div>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {meta.title}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    {meta.description}
                </p>
            </div>

            {message && (
                <div className={`mb-6 p-4 rounded-lg ${message.type === 'success'
                    ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                    : 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                    }`}>
                    {message.text}
                </div>
            )}

            <div className="space-y-6">
                <Card>
                    <div className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            {meta.globalSettingsTitle}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {meta.globalSettingsDescription}
                        </p>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.visitAnnouncedTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.visitAnnouncedDescription}
                                    </p>
                                </div>
                                <button
                                    onClick={handleToggleVisitAnnounced}
                                    disabled={updatingFlag || loadingSettings}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${settings.visit_announced ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                                        } ${updatingFlag || loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.visit_announced ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.internationalSignupsEnabledTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.internationalSignupsEnabledDescription}
                                    </p>
                                </div>
                                <button
                                    onClick={handleToggleInternationalSignups}
                                    disabled={updatingFlag || loadingSettings}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${settings.international_signups_enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                                        } ${updatingFlag || loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.international_signups_enabled ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.showShiftInfoTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.showShiftInfoDescription}
                                    </p>
                                </div>
                                <button
                                    onClick={handleToggleShowShiftInfo}
                                    disabled={updatingFlag || loadingSettings}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${settings.show_shift_info ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                                        } ${updatingFlag || loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.show_shift_info ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.localModeTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.localModeDescription}
                                    </p>
                                </div>
                                <button
                                    onClick={handleToggleLocalMode}
                                    disabled={updatingFlag || loadingSettings}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${settings.use_local_mode ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                                        } ${updatingFlag || loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.use_local_mode ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>

                    </div>
                </Card>

                <Card>
                    <div className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            {meta.trainingManagementTitle}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {meta.trainingManagementDescription}
                        </p>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.assignMandatoryTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.assignMandatoryDescription}
                                    </p>
                                </div>
                                <Button
                                    onClick={handleAssignMandatoryTrainings}
                                    disabled={loading}
                                    className="ms-4"
                                >
                                    {loading ? meta.assigningText : meta.assignButton}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            {meta.profileManagementTitle}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {meta.profileManagementDescription}
                        </p>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                                        {meta.clearProfilesTitle}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.clearProfilesDescription}
                                    </p>
                                </div>
                                <Button
                                    onClick={handleClearAllProfiles}
                                    disabled={clearingProfiles}
                                    variant="outline"
                                    className="ms-4 border-warn-500 text-warn-700 hover:bg-warn-50 dark:border-warn-600 dark:text-warn-400 dark:hover:bg-warn-950"
                                >
                                    {clearingProfiles ? meta.clearingProfilesText : meta.clearProfilesButton}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    )
}
